import React, { useRef, useState } from 'react';
import { Button, Typography } from '@comfama/comfama-ui-react';
import { setCargarExcelLibranzas } from '@/services/setCargarExcelLibranzas';
import GenericToast from '@/components/atoms/generic-toast/genericToast';
import './cargaMasiva.scss';

interface CargaMasivaResult {
    requestId: string;
    filas: any[];
}

interface LibranzaCargaMasivaProps {
    onSuccess?: (data: CargaMasivaResult) => void;
    interrogationDescription?: React.ReactNode;
}

const LibranzaCargaMasiva = ({ onSuccess, interrogationDescription }: LibranzaCargaMasivaProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [toast, setToast] = useState<React.ReactNode>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFileName(file.name);
            setToast(null);
            setUploadError(null);
        }
    };

    const handleUpload = () => {
        const file = inputRef.current?.files?.[0];
        if (!file) return;

        setIsUploading(true);
        setToast(null);
        setUploadError(null);

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            setCargarExcelLibranzas({ fileName: file.name, base64Content: base64 })
                .then((response) => {
                    if (response?.status === 200) {
                        setToast(
                            <GenericToast
                                setActiveToast={setToast}
                                isOk
                                message="Tu archivo se subió correctamente."
                                position="up-right"
                            />
                        );
                        onSuccess?.(response.data?.data ?? response.data);
                    } else if (response?.status === 400 || response?.status === 500) {
                        const userMessage: string = response.data?.userMessage ?? response.data?.data?.userMessage ?? '';
                        setUploadError(userMessage || 'El archivo no es válido. Por favor verifique el contenido e intente de nuevo.');
                    } else {
                        setToast(
                            <GenericToast
                                setActiveToast={setToast}
                                isOk={false}
                                message="No se pudo subir el archivo. Por favor intente de nuevo."
                                position="up-right"
                            />
                        );
                    }
                })
                .catch((error) => {
                    console.error('[carga-masiva]', error);
                    setToast(
                        <GenericToast
                            setActiveToast={setToast}
                            isOk={false}
                            message="Ocurrió un error inesperado al subir el archivo."
                            position="up-right"
                        />
                    );
                })
                .finally(() => {
                    setIsUploading(false);
                });
        };
        reader.onerror = () => {
            setToast(
                <GenericToast
                    setActiveToast={setToast}
                    isOk={false}
                    message="No se pudo leer el archivo. Por favor intente de nuevo."
                    position="up-right"
                />
            );
            setIsUploading(false);
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="m-carga-masiva">
            {interrogationDescription && (
                <div className="m-carga-masiva__description">
                    {interrogationDescription}
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                data-testid="carga-masiva-input"
            />
            <div className='m-carga-masiva__btn'>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => inputRef.current?.click()}
                    disabled={isUploading}
                >
                    Seleccionar archivo
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={!fileName || isUploading}
                >
                    {isUploading ? 'Subiendo...' : 'Subir'}
                </Button>
            </div>
            {fileName && (
                <>
                    <hr className="m-carga-masiva__divider" />
                    <Typography variant="body2" className="m-carga-masiva__file-name">
                        {fileName}
                    </Typography>
                </>
            )}
            {uploadError && (
                <Typography variant="body2" className="m-carga-masiva__upload-error">
                    {uploadError}
                </Typography>
            )}
            {toast}
        </div>
    );
};

export default LibranzaCargaMasiva;
